/**
 * Inline quick search for the chat sidebar and the Chat list header (people for DMs, channels).
 * A combobox: arrow keys move, Enter picks, Esc (or clearing it) closes. Results show once something is typed;
 * an optional footer (links such as "Browse all channels") sits under the results, outside the listbox.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Search, X } from 'lucide-react';

export interface QuickSearchProps<T> {
  rows: T[];
  q: string;
  onQ: (q: string) => void;
  onPick: (row: T) => void;
  onClose: () => void;
  rowKey: (row: T) => string;
  renderRow: (row: T, active: boolean) => ReactNode;
  /** Accessible name of the input, e.g. "Find a person to message". */
  label: string;
  /** Accessible name of the result list, e.g. "People". */
  listLabel: string;
  placeholder: string;
  emptyText: (q: string) => ReactNode;
  footer?: ReactNode;
  /** Show the footer before anything is typed too. */
  footerAlways?: boolean;
  tone?: 'dark' | 'light';
  testId: string;
  resultTestId: string;
}

export function QuickSearch<T>({ rows, q, onQ, onPick, onClose, rowKey, renderRow, label, listLabel, placeholder, emptyText, footer, footerAlways, tone = 'dark', testId, resultTestId }: QuickSearchProps<T>) {
  const [hi, setHi] = useState(0);
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const dark = tone === 'dark';
  const at = Math.min(hi, rows.length - 1);
  const active = at >= 0 ? rows[at] : undefined;
  const typed = q.trim().length > 0;
  // Keep the results in view in the scrolling sidebar.
  useEffect(() => { box.current?.scrollIntoView?.({ block: 'nearest' }); }, [rows.length, hi]);
  const optId = (i: number) => `${listId}-o${i}`;
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (active) onPick(active); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  };
  return (
    <div ref={box} className={clsx('relative', dark ? 'mx-2' : '')} data-testid={testId}
      onBlur={e => { if (!box.current?.contains(e.relatedTarget as Node) && !q) onClose(); }}>
      <div className={clsx('flex items-center gap-1.5 rounded-md border px-2 py-1', dark ? 'border-white/25 bg-white/5 focus-within:border-white' : 'border-slate-300 bg-white focus-within:border-slate-900 dark:border-slate-700 dark:bg-slate-900')}>
        <Search className={clsx('h-3.5 w-3.5 shrink-0', dark ? 'text-sidebar-muted' : 'text-slate-500')} aria-hidden />
        <input autoFocus value={q} onChange={e => { onQ(e.target.value); setHi(0); if (!e.target.value) onClose(); }} onKeyDown={onKey}
          role="combobox" aria-expanded={typed && rows.length > 0} aria-controls={listId} aria-autocomplete="list" aria-activedescendant={active && typed ? optId(at) : undefined}
          aria-label={label} placeholder={placeholder}
          className={clsx('min-w-0 flex-1 bg-transparent text-[13px] outline-none', dark ? 'text-white placeholder:text-sidebar-muted' : 'placeholder:text-slate-400')} />
        <button type="button" aria-label="Close search" title="Close (Esc)" onClick={onClose} className={clsx('rounded p-0.5', dark ? 'text-sidebar-muted hover:text-white' : 'text-slate-500 hover:text-slate-900')}><X className="h-3.5 w-3.5" /></button>
      </div>
      <ul id={listId} role="listbox" aria-label={listLabel} className={clsx('mt-1 space-y-px', !typed && 'hidden')}>
        {typed && rows.length === 0 && <li role="presentation" className={clsx('px-2 py-1.5 text-[13px]', dark ? 'text-sidebar-muted' : 'text-slate-500')}>{emptyText(q.trim())}</li>}
        {typed && rows.map((r, i) => {
          const on = i === at;
          return (
            <li key={rowKey(r)} id={optId(i)} role="option" aria-selected={on} data-testid={resultTestId}
              onMouseDown={e => e.preventDefault()} onMouseEnter={() => setHi(i)} onClick={() => onPick(r)}
              className={clsx('flex cursor-pointer items-center gap-2 rounded-md px-2 py-1', dark ? (on ? 'bg-sidebar-active text-white' : 'text-sidebar-fg hover:bg-sidebar-hover') : (on ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-900'))}>
              {renderRow(r, on)}
            </li>
          );
        })}
      </ul>
      {footer && (typed || footerAlways) && <div className={clsx('mt-1 flex flex-wrap gap-x-3 gap-y-1 border-t px-2 pt-1.5 text-[12px]', dark ? 'border-white/10 text-sidebar-muted' : 'border-slate-200 text-slate-600 dark:border-slate-800 dark:text-slate-400')}>{footer}</div>}
    </div>
  );
}
